/**
 * Step 11's public front page — CLAUDE.md's Public listings site section.
 * Plain useState/useEffect + the shared api() wrapper, exactly /apply's
 * pattern (not Refine's useList): this tree is actor-less, and GET
 * /api/listings is genuinely unauthenticated.
 */

import { useEffect, useState } from 'react';
import type { PublicListingDto } from 'shared';
import { api } from '../../lib/api.js';
import { Hero } from './Hero.js';
import { HomeBento } from './HomeBento.js';
import { ListingRow } from './ListingRow.js';
import { ListingsMap } from './ListingsMap.js';
import { PromoBanner } from './PromoBanner.js';

export function PublicHomepage() {
  const [listings, setListings] = useState<PublicListingDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api<{ data: PublicListingDto[] }>('/listings')
      .then((response) => {
        if (!cancelled) setListings(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load current listings. Please try again shortly.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing to show in this space once listings have loaded and there are
  // none published — no message, no empty state, the section (padding
  // included) just doesn't render. Still renders while loading or on error,
  // so those states are visible.
  const showListingsSection = error !== null || listings === null || listings.length > 0;

  return (
    <>
      <PromoBanner />
      <Hero />
      {showListingsSection ? (
        <section className="public__listings" aria-labelledby="public-listings-heading">
          <h2 id="public-listings-heading" className="visually-hidden">
            Current listings
          </h2>
          {error ? <p className="alert alert--error">{error}</p> : null}
          {listings === null && !error ? (
            <p className="muted public__empty">Loading listings…</p>
          ) : null}
          {listings ? <ListingRow listings={listings} /> : null}
        </section>
      ) : null}
      {listings && listings.length > 0 ? <ListingsMap listings={listings} /> : null}
      <HomeBento />
    </>
  );
}
